var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Type } from '@sinclair/typebox';
import { postWithRetries } from "../../http.mjs";
import { logger } from "../../log.mjs";
import { elementInstance } from "../instance.mjs";
import { confirmPaymentIntentResponseSchemaV2 } from "../../psp/stripe/schema.mjs";
import { completeInvoicePurchase } from "../../completeInvoicePurchase.mjs";
import { mapStripeErrorCode } from "../../psp/stripe/errors.mjs";
import { paymentElementId } from "./elements.mjs";
import { getBillingAddress, getShippingAddress, loadStripeIfNeeded } from "./utils.mjs";
import { getCurrentBillingAddressElement } from "./billingAddressElements.mjs";
import { getCurrentShippingAddressElement } from "./shippingAddressElements.mjs";
export const initializeStripePaymentElements = (state, actions, cartPayment, elements) => {
    elementInstance({
        elementId: paymentElementId,
        idempotencyKey: state.id + elements.id,
        onMount: (e) => __awaiter(void 0, void 0, void 0, function* () {
            const stripe = yield loadStripeIfNeeded(cartPayment);
            const paymentElement = getOrCreatePaymentElement(state, elements);
            paymentElement.mount(e);
            actions.setCompletePurchaseHandler(() => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                const shippingElement = getCurrentShippingAddressElement();
                const billingElement = getCurrentBillingAddressElement();
                if (!shippingElement) {
                    state.onUnexpectedError('Error retrieving shipping address element');
                    return;
                }
                const shippingDetails = yield shippingElement.getValue();
                const billingDetails = yield (billingElement === null || billingElement === void 0 ? void 0 : billingElement.getValue());
                if (!shippingDetails.complete) {
                    return {
                        $type: 'validationError',
                        errors: [{ message: 'Complete shipping address not provided' }],
                    };
                }
                let billingDetailsFinal = Object.assign({}, shippingDetails);
                if (state.customer.useBillingAddress) {
                    if (!(billingDetails === null || billingDetails === void 0 ? void 0 : billingDetails.complete)) {
                        return {
                            $type: 'validationError',
                            errors: [{ message: `Complete billing address not provided` }],
                        };
                    }
                    billingDetailsFinal = Object.assign({}, billingDetails);
                }
                yield savePaymentIntentCustomerData(state, shippingDetails, billingDetails);
                const { error: submitError, selectedPaymentMethod } = yield elements.submit();
                if (submitError) {
                    return {
                        $type: 'validationError',
                        errors: [{ message: (_a = submitError.message) !== null && _a !== void 0 ? _a : 'An unexpected error occurred' }],
                    };
                }
                // Paid by invoice
                if (selectedPaymentMethod === 'cpmt_1S8gpfGklE6B0bhsP58oClpK') {
                    yield completeInvoicePurchase(state, actions);
                    window.location.href = state.orderConfirmationPageUrl + '?orderId=' + state.orderId;
                }
                const clientSecret = cartPayment.elements.paymentIntentClientSecret;
                const { paymentIntent, error: paymentIntentError } = yield stripe.retrievePaymentIntent(clientSecret);
                if (paymentIntentError) {
                    state.onUnexpectedError(paymentIntentError.message || 'Error retrieving payment intent');
                    return;
                }
                if (paymentIntent.status === 'canceled') {
                    return {
                        $type: 'failed',
                        errors: [{ type: 'paymentError', code: 'payment_invalid', message: 'Payment intent has a status of canceled' }],
                    };
                }
                const { confirmationToken, error: confirmationTokenError } = yield stripe.createConfirmationToken({
                    elements,
                    params: getConfirmParams(state, billingDetailsFinal),
                });
                if (confirmationTokenError) {
                    state.onUnexpectedError(confirmationTokenError.message || 'Error creating confirmation token');
                    return;
                }
                const response = yield confirmPaymentIntent(state, {
                    paymentIntentId: paymentIntent.id,
                    confirmationTokenId: confirmationToken.id,
                });
                if (!response) {
                    return;
                }
                if (response.status === 'failed') {
                    const { code, message } = response.error;
                    return {
                        $type: 'failed',
                        errors: [
                            {
                                type: 'paymentError',
                                code: code ? mapStripeErrorCode(code) : 'unknown',
                                message,
                            },
                        ],
                    };
                }
                const redirectToConfirmationPage = (status) => {
                    window.location.href = `${state.checkoutPageUrl}?payment_intent=${paymentIntent.id}&payment_intent_client_secret=${response.paymentIntentClientSecret}&redirect_status=${status}`;
                };
                switch (response.paymentIntentStatus) {
                    case 'succeeded':
                    case 'processing':
                        return redirectToConfirmationPage(response.paymentIntentStatus);
                    case 'requires_action': {
                        const { error, paymentIntent } = yield stripe.handleNextAction({ clientSecret: response.paymentIntentClientSecret });
                        if (error) {
                            const { code, message } = error;
                            return {
                                $type: 'failed',
                                errors: [
                                    {
                                        type: 'paymentError',
                                        code: code ? mapStripeErrorCode(code) : 'unknown',
                                        message: message || 'Next action handling failed',
                                    },
                                ],
                            };
                        }
                        // Status will be either processing or succeeded, according to the docs
                        // https://docs.stripe.com/js/payment_intents/handle_next_action
                        const newStatus = paymentIntent.status;
                        return redirectToConfirmationPage(newStatus);
                    }
                    default:
                        state.onUnexpectedError('Payment could not be processed');
                        return;
                }
            }));
            return () => paymentElement.unmount();
        }),
    });
};
const getConfirmParams = (state, billingDetails) => {
    return {
        payment_method_data: {
            billing_details: {
                name: billingDetails.value.name,
                email: state.customer.email,
                phone: state.customer.phoneNumber,
                address: {
                    line1: billingDetails.value.address.line1,
                    line2: billingDetails.value.address.line2,
                    city: billingDetails.value.address.city,
                    state: billingDetails.value.address.state,
                    postal_code: billingDetails.value.address.postal_code,
                    country: billingDetails.value.address.country,
                },
            },
        },
        // Stripe will include query params to this url that we pick up on and check for a
        // status update on the payment intent
        return_url: state.checkoutPageUrl,
    };
};
const getPaymentElement = (elements) => {
    return elements.getElement('payment');
};
const getOrCreatePaymentElement = (state, elements) => {
    return getPaymentElement(elements) || createPaymentElement(state, elements);
};
const createPaymentElement = (state, elements) => {
    var _a, _b;
    const { layout } = (_b = (_a = state.paymentProviderElementsOptions.stripe) === null || _a === void 0 ? void 0 : _a.paymentElement) !== null && _b !== void 0 ? _b : {};
    return elements.create('payment', {
        fields: { billingDetails: { address: 'if_required' } },
        layout,
    });
};
const savePaymentIntentCustomerData = (state, shippingDetails, billingDetails) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const body = {
        address: getShippingAddress(shippingDetails),
        billingAddress: billingDetails ? getBillingAddress(billingDetails) : null,
        email: (_a = state.customer.email) !== null && _a !== void 0 ? _a : null,
        phoneNumber: (_b = state.customer.phoneNumber) !== null && _b !== void 0 ? _b : null,
    };
    try {
        yield postWithRetries(`/carts/${state.id}/${state.version}/payment/customer`, state.checkoutApiBaseUrl, body, Type.Unknown(), state.token);
    }
    catch (error) {
        logger.error({ error }, 'An unexpected error occurred');
        state.onUnexpectedError('An unexpected error occurred');
    }
});
const confirmPaymentIntent = (state, body) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        return yield postWithRetries('/stripe/payments/confirm/v2', state.checkoutApiBaseUrl, body, confirmPaymentIntentResponseSchemaV2, state.token);
    }
    catch (error) {
        logger.error({ error }, 'An unexpected error occurred');
        state.onUnexpectedError('An unexpected error occurred');
        return null;
    }
});
//# sourceMappingURL=paymentElements.js.map